// Route-tree wiring only.
//   - Loaders live in `src/lib/route-loaders.ts` (prefetch helpers, `defaultPreload: "intent"`).
//   - Guards live in `src/components/guards/` (`AuthGuard`, `GuestGuard`, `AdminGuard`,
//     `OnboardingGuard`, `TermsGuard`).
//   - Lazy page imports live in `src/routes/lazy-pages.ts`.
// Do not inline business logic, data-fetching, or guard implementations in this file —
// add them to the modules above and reference them here. Audited 2026-05-15.
import {
	createRootRoute,
	createRoute,
	Outlet,
	redirect,
} from "@tanstack/react-router";
import { Suspense } from "react";
import {
	AdminGuard,
	AuthGuard,
	GuestGuard,
	OnboardingGuard,
	TermsGuard,
} from "./components/guards";
import {
	forgetListSearchOutside,
	LIST_FILTER_WRITE,
	parseAdminListSearch,
	restoreListSearch,
} from "./lib/admin-list-search";
import { AdminLayout } from "./components/layout/admin-layout";
import { DashboardLayout } from "./components/layout/dashboard-layout";
import {
	prefetchAdminBlogCategories,
	prefetchAdminBlogEdit,
	prefetchAdminBlogSuggestions,
	prefetchAdminBlogs,
	prefetchAdminDashboard,
	prefetchAdminProAnalytics,
	prefetchAdminProCompanyProfile,
	prefetchAdminProDetail,
	prefetchAdminProjects,
	prefetchAdminPros,
	prefetchAdminRoomCategories,
	prefetchAdminTaxonomy,
	prefetchAdminUserDetail,
	prefetchAdminUsers,
	prefetchAnalytics,
	prefetchBlogEdit,
	prefetchBlogs,
	prefetchCreateReel,
	prefetchCrmAnalytics,
	prefetchCrmKanban,
	prefetchCrmLeadDetail,
	prefetchCrmSettings,
	prefetchDashboard,
	prefetchNotifications,
	prefetchProfile,
	prefetchProjectDetail,
	prefetchProjects,
	prefetchSettings,
	prefetchSocialDraft,
	prefetchSocialDrafts,
	prefetchTeam,
	prefetchWebsite,
} from "./lib/route-loaders";
import {
	AcceptInvitationPage,
	AcceptTermsPage,
	AdminAnalyticsPage,
	AdminBlogCategoriesPage,
	AdminBlogCreatePage,
	AdminBlogEditPage,
	AdminBlogListPage,
	AdminBlogSuggestionsPage,
	AdminCalculatorConfigPage,
	AdminCommunicationsPage,
	AdminConsultationsPage,
	AdminWebLeadsPage,
	AdminDashboardPage,
	AdminFeedbackPage,
	AdminHomePagePage,
	AdminImportsDetailPage,
	AdminImportsNewPage,
	AdminImportsPage,
	AdminNotificationTestPage,
	AdminProAnalyticsPage,
	AdminProCompanyProfilePage,
	AdminProDetailPage,
	AdminProjectDetailPage,
	AdminProjectsPage,
	AdminProsPage,
	AdminRoomCategoriesPage,
	AdminRoomCategoryDetailPage,
	AdminSearchPage,
	AdminSocialStudioMusicPage,
	AdminSocialStudioTemplatesPage,
	AdminTaxonomyPage,
	AdminUserDetailPage,
	AdminUserSecurityPage,
	AdminUsersPage,
	AdminWhatsAppCampaignCreatePage,
	AdminWhatsAppCampaignDetailPage,
	AdminWhatsAppCampaignsPage,
	AdminWhatsAppDashboardPage,
	AdminWhatsAppInboxPage,
	AdminWhatsAppSendPage,
	AdminWhatsAppTemplatesPage,
	InsightsPage,
	AuthPage,
	StudioCreatePage,
	PipelineInsightsPage,
	LeadDetailPage,
	LeadsPage,
	PipelineSettingsPage,
	DashboardPage,
	EditBlogPage,
	InternalMetricsPage,
	NewBlogPage,
	NotificationsPage,
	OnboardingPage,
	ProfilePage,
	ProjectDetailsPage,
	ProjectsPage,
	ProMyBlogsPage,
	ResetPasswordPage,
	SecuritySettingsPage,
	SettingsPage,
	StudioDraftPage,
	StudioPage,
	TeamPage,
	VerifyEmailPage,
	WebsitePage,
} from "./routes/lazy-pages";

// Content-area loading fallback (NOT full-screen -- layout is already mounted)
const ContentLoading = () => (
	<div className="flex items-center justify-center h-64">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
	</div>
);

// Full-screen loading fallback (for standalone routes without a parent layout)
const FullScreenLoading = () => (
	<div className="flex items-center justify-center h-screen">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
	</div>
);

// ─── Root Route ──────────────────────────────────────────────────────────────
export const rootRoute = createRootRoute({
	component: () => <Outlet />,
});

// ─── Pro Layout Route (pathless) ──────────────────────────────────────────
// AuthGuard + OnboardingGuard + DashboardLayout stay mounted across all pro routes.
// Only the <Outlet /> content area swaps on navigation.
const proLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_proLayout",
	component: () => (
		<AuthGuard>
			<TermsGuard>
				<OnboardingGuard>
					<DashboardLayout>
						<Suspense fallback={<ContentLoading />}>
							<div className="animate-page-in">
								<Outlet />
							</div>
						</Suspense>
					</DashboardLayout>
				</OnboardingGuard>
			</TermsGuard>
		</AuthGuard>
	),
});

// ─── Guest Layout Route (pathless) ───────────────────────────────────────────
const guestLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_guestLayout",
	component: () => (
		<GuestGuard>
			<Suspense fallback={<FullScreenLoading />}>
				<Outlet />
			</Suspense>
		</GuestGuard>
	),
});

// ─── Admin Layout Route (pathless) ───────────────────────────────────────────
const adminLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_adminLayout",
	beforeLoad: ({ location, cause }) =>
		forgetListSearchOutside(location.pathname, cause),
	component: () => (
		<AdminGuard>
			<AdminLayout>
				<Suspense fallback={<ContentLoading />}>
					<div className="animate-page-in">
						<Outlet />
					</div>
				</Suspense>
			</AdminLayout>
		</AdminGuard>
	),
});

// ─── Pro Child Routes ─────────────────────────────────────────────────────
// No guards or layout wrappers needed -- parent layout provides them.

export const indexRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/",
	loader: () => prefetchDashboard(),
	component: () => <DashboardPage />,
});

export const projectsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/projects",
	loader: () => prefetchProjects(),
	component: () => <ProjectsPage />,
});

export const projectDetailsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/projects/$projectId",
	validateSearch: (search) => ({
		tab: (search.tab as string) || "",
	}),
	loader: ({ params }) => prefetchProjectDetail(params.projectId),
	component: () => <ProjectDetailsPage />,
});

export const profileRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings/studio",
	validateSearch: (search) => ({
		tab: (search.tab as string) || "basics",
	}),
	loader: () => prefetchProfile(),
	component: () => <ProfilePage />,
});

export const accountSettingsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings/account",
	loader: () => prefetchSettings(),
	component: () => <SettingsPage />,
});

export const securitySettingsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings/security",
	component: () => <SecuritySettingsPage />,
});

export const teamRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings/team",
	loader: () => prefetchTeam(),
	component: () => <TeamPage />,
});

export const insightsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/insights",
	loader: () => prefetchAnalytics(),
	component: () => <InsightsPage />,
});

export const blogRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blog",
	loader: () => prefetchBlogs(),
	component: () => <ProMyBlogsPage />,
});

export const newBlogRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blog/new",
	component: () => <NewBlogPage />,
});

export const editBlogRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blog/$id/edit",
	loader: ({ params }) => prefetchBlogEdit(params.id),
	component: () => <EditBlogPage />,
});

export const websiteRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/website",
	loader: () => prefetchWebsite(),
	component: () => <WebsitePage />,
});

export const leadsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/leads",
	loader: () => prefetchCrmKanban(),
	component: () => <LeadsPage />,
});

export const leadDetailRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/leads/$leadId",
	loader: ({ params }) => prefetchCrmLeadDetail(params.leadId),
	component: () => <LeadDetailPage />,
});

export const pipelineInsightsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/insights/pipeline",
	loader: () => prefetchCrmAnalytics(),
	component: () => <PipelineInsightsPage />,
});

export const pipelineSettingsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings/pipeline",
	loader: () => prefetchCrmSettings(),
	component: () => <PipelineSettingsPage />,
});

export const notificationsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/notifications",
	loader: () => prefetchNotifications(),
	component: () => <NotificationsPage />,
});

export const studioRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/studio",
	validateSearch: (search) => ({
		highlight: (search.highlight as string) || undefined,
	}),
	loader: () => prefetchSocialDrafts(),
	component: () => <StudioPage />,
});

export const studioDraftRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/studio/$draftId",
	loader: ({ params }) => prefetchSocialDraft(params.draftId),
	component: () => <StudioDraftPage />,
});

export const studioCreateRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/studio/create",
	validateSearch: (search) => ({
		projectId: (search.projectId as string) || undefined,
	}),
	loader: () => prefetchCreateReel(),
	component: () => <StudioCreatePage />,
});

// ─── Legacy Pro Path Redirects ───────────────────────────────────────────────
// Phase 3.4 renamed nine pro paths. Every old one stays registered and
// redirects instead of 404ing, because a renamed URL is still live in
// somebody's bookmarks, in mail we already sent, and in the `data.url` of
// every queued push notification — `apps/api` still mints `/crm/leads/:id`
// for reminder and new-inquiry pushes. Deleting the old path breaks those
// silently and the only signal is a pro who stops clicking.
//
// Path params and the whole query string ride along, so a deep link lands on
// the same row it always did: `/crm/leads/42?tab=notes` -> `/leads/42?tab=notes`.
//
// `validateSearch` is a pass-through rather than a schema on purpose — the job
// is to forward whatever was there, including params added after this was
// written. `replace: true` keeps the dead URL out of history, so Back goes
// where the reader came from rather than bouncing through the redirect again.
function legacyRedirect(from: string, to: string) {
	return createRoute({
		getParentRoute: () => proLayoutRoute,
		path: from,
		validateSearch: (search: Record<string, unknown>) => search,
		beforeLoad: ({ params, search }) => {
			throw redirect({ to, params, search, replace: true });
		},
	});
}

export const legacyCrmRoute = legacyRedirect("/crm", "/leads");
export const legacyCrmLeadDetailRoute = legacyRedirect(
	"/crm/leads/$leadId",
	"/leads/$leadId",
);
export const legacyCrmAnalyticsRoute = legacyRedirect(
	"/crm/analytics",
	"/insights/pipeline",
);
export const legacyCrmSettingsRoute = legacyRedirect(
	"/crm/settings",
	"/settings/pipeline",
);
export const legacyAnalyticsRoute = legacyRedirect("/analytics", "/insights");
export const legacyProfileRoute = legacyRedirect(
	"/profile",
	"/settings/studio",
);
export const legacyTeamRoute = legacyRedirect("/team", "/settings/team");
export const legacyBlogsRoute = legacyRedirect("/blogs", "/blog");
export const legacyNewBlogRoute = legacyRedirect("/blogs/new", "/blog/new");
export const legacyEditBlogRoute = legacyRedirect(
	"/blogs/$id/edit",
	"/blog/$id/edit",
);
export const legacySocialStudioRoute = legacyRedirect(
	"/social-studio",
	"/studio",
);
export const legacySocialStudioCreateRoute = legacyRedirect(
	"/social-studio/create",
	"/studio/create",
);
export const legacySocialStudioDraftRoute = legacyRedirect(
	"/social-studio/$draftId",
	"/studio/$draftId",
);

// Not a rename: `/settings` is now a section with no page of its own, so it
// sends you to the first thing in it instead of rendering a blank shell.
export const settingsIndexRoute = legacyRedirect(
	"/settings",
	"/settings/account",
);

/** Registered in route order; also the checklist the redirect tests walk. */
export const LEGACY_REDIRECTS = [
	legacyCrmRoute,
	legacyCrmLeadDetailRoute,
	legacyCrmAnalyticsRoute,
	legacyCrmSettingsRoute,
	legacyAnalyticsRoute,
	legacyProfileRoute,
	legacyTeamRoute,
	legacyBlogsRoute,
	legacyNewBlogRoute,
	legacyEditBlogRoute,
	legacySocialStudioRoute,
	legacySocialStudioCreateRoute,
	legacySocialStudioDraftRoute,
	settingsIndexRoute,
] as const;

// ─── Guest Child Routes ──────────────────────────────────────────────────────

export const loginRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/login",
	validateSearch: (search) => ({
		redirect: (search.redirect as string) || undefined,
	}),
	beforeLoad: ({ search }) => {
		throw redirect({ to: "/auth/sign-in", search });
	},
});

export const registerRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/register",
	validateSearch: (search) => ({
		email: (search.email as string) || undefined,
		redirect: (search.redirect as string) || undefined,
		token: (search.token as string) || undefined,
	}),
	beforeLoad: ({ search }) => {
		throw redirect({ to: "/auth/sign-up", search });
	},
});

export const forgotPasswordRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/forgot-password",
	beforeLoad: () => {
		throw redirect({ to: "/auth/forgot-password" });
	},
});

export const resetPasswordRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/reset-password",
	component: () => <ResetPasswordPage />,
});

export const verifyEmailRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/verify-email",
	component: () => <VerifyEmailPage />,
});

function AuthPageWrapper() {
	const { pathname } = authRoute.useParams();
	const search = authRoute.useSearch();
	return (
		<AuthPage
			pathname={pathname}
			search={search as Record<string, string | undefined>}
		/>
	);
}

export const authRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/auth/$pathname",
	validateSearch: (search) => ({
		redirect: (search.redirect as string) || undefined,
		email: (search.email as string) || undefined,
		token: (search.token as string) || undefined,
		error: (search.error as string) || undefined,
	}),
	component: () => <AuthPageWrapper />,
});

// ─── Admin Child Routes ──────────────────────────────────────────────────────

export const adminDashboardRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin",
	loader: () => prefetchAdminDashboard(),
	component: () => <AdminDashboardPage />,
});

export const adminProsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros",
	validateSearch: parseAdminListSearch,
	beforeLoad: ({ search, cause, location }) =>
		restoreListSearch(
			"/admin/pros",
			search,
			cause,
			LIST_FILTER_WRITE in location.state,
		),
	loader: () => prefetchAdminPros(),
	component: () => <AdminProsPage />,
});

export const adminProDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros/$proId",
	loader: ({ params }) => prefetchAdminProDetail(params.proId),
	component: () => <AdminProDetailPage />,
});

export const adminProAnalyticsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros/$proId/analytics",
	loader: ({ params }) => prefetchAdminProAnalytics(params.proId),
	component: () => <AdminProAnalyticsPage />,
});

export const adminProCompanyProfileRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros/$proId/company-profile",
	loader: ({ params }) => prefetchAdminProCompanyProfile(params.proId),
	component: () => <AdminProCompanyProfilePage />,
});

export const adminProjectsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/projects",
	validateSearch: parseAdminListSearch,
	beforeLoad: ({ search, cause, location }) =>
		restoreListSearch(
			"/admin/projects",
			search,
			cause,
			LIST_FILTER_WRITE in location.state,
		),
	loader: () => prefetchAdminProjects(),
	component: () => <AdminProjectsPage />,
});

export const adminImportsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/imports",
	component: () => <AdminImportsPage />,
});

export const adminImportsNewRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/imports/new",
	component: () => <AdminImportsNewPage />,
});

export const adminImportsDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/imports/$importId",
	component: () => <AdminImportsDetailPage />,
});

export const adminProjectDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/projects/$projectId",
	component: () => <AdminProjectDetailPage />,
});

export const adminUsersRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/users",
	loader: () => prefetchAdminUsers(),
	component: () => <AdminUsersPage />,
});

export const adminUserDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/users/$userId",
	loader: ({ params }) => prefetchAdminUserDetail(params.userId),
	component: () => <AdminUserDetailPage />,
});

export const adminUserSecurityRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/users/$userId/security",
	component: () => <AdminUserSecurityPage />,
});

export const adminTaxonomyRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/taxonomy",
	loader: () => prefetchAdminTaxonomy(),
	component: () => <AdminTaxonomyPage />,
});

export const adminAnalyticsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/analytics",
	component: () => <AdminAnalyticsPage />,
});

export const adminCommunicationsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/communications",
	component: () => <AdminCommunicationsPage />,
});

export const adminFeedbackRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/feedback",
	component: () => <AdminFeedbackPage />,
});

export const adminConsultationsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/consultations",
	component: () => <AdminConsultationsPage />,
});

export const adminWebLeadsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/web-leads",
	component: () => <AdminWebLeadsPage />,
});

export const adminBlogsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs",
	loader: () => prefetchAdminBlogs(),
	component: () => <AdminBlogListPage />,
});

export const adminBlogCreateRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/create",
	component: () => <AdminBlogCreatePage />,
});

export const adminBlogEditRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/$id/edit",
	loader: ({ params }) => prefetchAdminBlogEdit(params.id),
	component: () => <AdminBlogEditPage />,
});

export const adminBlogCategoriesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/categories",
	loader: () => prefetchAdminBlogCategories(),
	component: () => <AdminBlogCategoriesPage />,
});

export const adminBlogSuggestionsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/suggestions",
	loader: () => prefetchAdminBlogSuggestions(),
	component: () => <AdminBlogSuggestionsPage />,
});

// ─── Admin WhatsApp Routes ───────────────────────────────────────────────────

export const adminWhatsAppRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp",
	component: () => <AdminWhatsAppDashboardPage />,
});

export const adminWhatsAppInboxRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/inbox",
	validateSearch: (search) => ({
		id: (search.id as number) || undefined,
		// The dashboard's Unread card links here with `?view=unread`. The card
		// used to point at an inbox that could not filter to unread at all —
		// the filter existed in the DAL and was never parsed by the route.
		view: search.view === "unread" ? ("unread" as const) : undefined,
	}),
	component: () => <AdminWhatsAppInboxPage />,
});

export const adminWhatsAppTemplatesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/templates",
	component: () => <AdminWhatsAppTemplatesPage />,
});

export const adminWhatsAppCampaignsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/campaigns",
	component: () => <AdminWhatsAppCampaignsPage />,
});

export const adminWhatsAppCampaignCreateRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/campaigns/create",
	validateSearch: (search) => ({
		viewId: (search.viewId as number) || undefined,
	}),
	component: () => <AdminWhatsAppCampaignCreatePage />,
});

/**
 * The campaign read view, at its own URL.
 *
 * `/campaigns/create` and `/campaigns/$campaignId` sit at the same depth. The
 * router ranks a static segment above a dynamic one, so "create" keeps
 * resolving to the wizard — asserted in `routes.navigation.test.tsx` rather
 * than assumed, because it is the kind of thing a router upgrade changes
 * quietly.
 */
export const adminWhatsAppCampaignDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/campaigns/$campaignId",
	component: () => <AdminWhatsAppCampaignDetailPage />,
});

export const adminWhatsAppSendRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/send",
	component: () => <AdminWhatsAppSendPage />,
});

export const adminNotificationTestRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/notifications/test",
	component: () => <AdminNotificationTestPage />,
});

export const adminSearchRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/search",
	component: () => <AdminSearchPage />,
});

export const adminSocialStudioMusicRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/social-studio/music",
	component: () => <AdminSocialStudioMusicPage />,
});

export const adminSocialStudioTemplatesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/social-studio/templates",
	component: () => <AdminSocialStudioTemplatesPage />,
});

export const adminRoomCategoriesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/room-categories",
	loader: () => prefetchAdminRoomCategories(),
	component: () => <AdminRoomCategoriesPage />,
});

export const adminRoomCategoryDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/room-categories/$code",
	component: () => <AdminRoomCategoryDetailPage />,
});

export const adminHomePageRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/home-page",
	component: () => <AdminHomePagePage />,
});

export const adminCalculatorConfigRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/calculator-config",
	component: () => <AdminCalculatorConfigPage />,
});

// ─── Standalone Routes (no parent layout) ────────────────────────────────────

export const onboardingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/onboarding",
	component: () => (
		<AuthGuard>
			<Suspense fallback={<FullScreenLoading />}>
				<OnboardingPage />
			</Suspense>
		</AuthGuard>
	),
});

export const acceptInvitationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/accept-invitation",
	component: () => (
		<Suspense fallback={<FullScreenLoading />}>
			<AcceptInvitationPage />
		</Suspense>
	),
});

export const acceptTermsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/accept-terms",
	component: () => (
		<AuthGuard>
			<Suspense fallback={<FullScreenLoading />}>
				<AcceptTermsPage />
			</Suspense>
		</AuthGuard>
	),
});

// Public leadership-metrics dashboard. No auth — shared via URL with leadership.
export const internalMetricsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/internal/metrics",
	component: () => (
		<Suspense fallback={<FullScreenLoading />}>
			<InternalMetricsPage />
		</Suspense>
	),
});

// ─── Route Tree ──────────────────────────────────────────────────────────────
export const routeTree = rootRoute.addChildren([
	// Pro layout (pathless) with nested pro routes
	proLayoutRoute.addChildren([
		indexRoute,
		projectsRoute,
		projectDetailsRoute,
		profileRoute,
		accountSettingsRoute,
		securitySettingsRoute,
		teamRoute,
		insightsRoute,
		blogRoute,
		newBlogRoute,
		editBlogRoute,
		websiteRoute,
		leadsRoute,
		leadDetailRoute,
		pipelineInsightsRoute,
		pipelineSettingsRoute,
		notificationsRoute,
		studioRoute,
		studioCreateRoute,
		studioDraftRoute,
		...LEGACY_REDIRECTS,
	]),
	// Guest layout (pathless) with nested guest routes
	guestLayoutRoute.addChildren([
		loginRoute,
		registerRoute,
		forgotPasswordRoute,
		resetPasswordRoute,
		verifyEmailRoute,
		authRoute,
	]),
	// Admin layout (pathless) with nested admin routes
	adminLayoutRoute.addChildren([
		adminDashboardRoute,
		adminProsRoute,
		adminProDetailRoute,
		adminProAnalyticsRoute,
		adminProCompanyProfileRoute,
		adminProjectsRoute,
		adminProjectDetailRoute,
		adminImportsRoute,
		adminImportsNewRoute,
		adminImportsDetailRoute,
		adminUsersRoute,
		adminUserDetailRoute,
		adminUserSecurityRoute,
		adminTaxonomyRoute,
		adminAnalyticsRoute,
		adminCommunicationsRoute,
		adminFeedbackRoute,
		adminConsultationsRoute,
		adminWebLeadsRoute,
		adminBlogsRoute,
		adminBlogCreateRoute,
		adminBlogEditRoute,
		adminBlogCategoriesRoute,
		adminBlogSuggestionsRoute,
		adminWhatsAppRoute,
		adminWhatsAppInboxRoute,
		adminWhatsAppTemplatesRoute,
		adminWhatsAppCampaignsRoute,
		adminWhatsAppCampaignCreateRoute,
		adminWhatsAppCampaignDetailRoute,
		adminWhatsAppSendRoute,
		adminNotificationTestRoute,
		adminSearchRoute,
		adminSocialStudioMusicRoute,
		adminSocialStudioTemplatesRoute,
		adminRoomCategoriesRoute,
		adminRoomCategoryDetailRoute,
		adminHomePageRoute,
		adminCalculatorConfigRoute,
	]),
	// Standalone routes
	onboardingRoute,
	acceptInvitationRoute,
	acceptTermsRoute,
	internalMetricsRoute,
]);
